/**
 * Yearly Demand overview: each piece answers one question.
 *   "What changed?"           Growing / Shrinking most, last full year vs the one before (top 3 each)
 *   "What are the numbers?"   one table, one row per product line (or per part when a tab has no lines)
 *   "How is this line doing?" click a row: side panel with its labelled yearly chart and its parts
 */
import { useMemo, useState } from "react";
import type { DemandTableRow } from "@march/shared";
import { fmtNum } from "../lib/csv.ts";
import { partName, skuFacets } from "../lib/skuNames.ts";
import { change, totalsOf, YearChart, type Totals } from "./GroupedDemand.tsx";

type Item = { key: string; label: string; rows: DemandTableRow[]; t: Totals };

const short = (fy: string) => fy.replace(/^FY20/, "FY");

export function DemandOverview({
  rows, fys, currentFy, groupOf, onBreakdown, unit,
}: {
  rows: DemandTableRow[];
  fys: string[];
  currentFy: string;
  groupOf: (itemid: string) => { key: string; label: string };
  onBreakdown: (itemid: string, fy: string) => void;
  unit: string;
}) {
  const full = fys.filter((f) => f !== currentFy).slice(-3);
  const [prev, last] = full.slice(-2);

  // Product lines when the tab has them; otherwise every part is its own row.
  const items: Item[] = useMemo(() => {
    const m = new Map<string, { label: string; rows: DemandTableRow[] }>();
    for (const r of rows) {
      const g = groupOf(r.itemid);
      if (!m.has(g.key)) m.set(g.key, { label: g.label, rows: [] });
      m.get(g.key)!.rows.push(r);
    }
    const groups = [...m.entries()];
    const list = groups.length > 1
      ? groups.map(([key, g]) => ({ key, label: g.label, rows: g.rows }))
      : rows.map((r) => ({ key: r.itemid, label: r.itemid, rows: [r] }));
    return list
      .map((x) => ({ ...x, t: totalsOf(x.rows, fys, currentFy) }))
      .sort((a, b) => (b.t.used[last] ?? 0) - (a.t.used[last] ?? 0));
  }, [rows, fys, currentFy, groupOf, last]);
  const byPart = items.length > 0 && items[0].rows.length === 1 && items.every((x) => x.rows.length === 1);

  const [sel, setSel] = useState<string | null>(null);
  const selected = items.find((x) => x.key === sel) ?? null;
  const [groupBy, setGroupBy] = useState<"size" | "finish">("size");
  const isCover = selected?.rows.some((r) => r.itemid.startsWith("C")) ?? false;
  const finishWord = isCover ? "By fabric" : "By finish";
  // The selected line's parts grouped by size or finish, biggest group first, biggest part first.
  const partGroups = useMemo(() => {
    if (!selected) return [];
    const m = new Map<string, DemandTableRow[]>();
    for (const r of selected.rows) {
      const f = skuFacets(r.itemid);
      const k = f ? (groupBy === "size" ? f.size : f.finish) : "Other";
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    return [...m.entries()]
      .map(([label, rs]) => ({ label, rows: rs.sort((a, b) => (b.fy_totals[last] ?? 0) - (a.fy_totals[last] ?? 0)), t: totalsOf(rs, fys, currentFy) }))
      .sort((a, b) => (b.t.used[last] ?? 0) - (a.t.used[last] ?? 0));
  }, [selected, groupBy, fys, currentFy, last]);

  // Units: the biggest moves in volume. %: the biggest relative moves, among lines that sold 100+ the year before.
  const [by, setBy] = useState<"units" | "pct">("units");
  const delta = (x: Item) => {
    const a = x.t.used[prev] ?? 0, b = x.t.used[last] ?? 0;
    return by === "units" ? b - a : a > 0 ? (b - a) / a : 0;
  };
  const ranked = [...items].filter((x) => (x.t.used[prev] ?? 0) >= (by === "pct" ? 100 : 1)).sort((a, b) => delta(b) - delta(a));
  const growing = ranked.filter((x) => delta(x) > 0).slice(0, 3);
  const shrinking = ranked.filter((x) => delta(x) < 0).slice(-3).reverse();

  const Chg = ({ t }: { t: Totals }) => {
    const c = change(t, fys, currentFy);
    return c ? <span className={`ychg ${c.cls}`}>{c.text}</span> : <span className="muted">—</span>;
  };
  const Mover = ({ x }: { x: Item }) => {
    const a = x.t.used[prev] ?? 0, b = x.t.used[last] ?? 0, m = Math.max(a, b, 1);
    const y = (v: number) => 22 - (v / m) * 18;
    const up = b >= a;
    return (
      <button type="button" className="mv" onClick={() => (byPart ? onBreakdown(x.key, last) : setSel(x.key))}>
        <span className="mv-name">{x.label}</span>
        <svg width="64" height="26" aria-hidden="true">
          <circle cx="7" cy={y(a)} r="3" className="mv-a" />
          <line x1="7" y1={y(a)} x2="57" y2={y(b)} className={up ? "mv-up" : "mv-down"} />
          <circle cx="57" cy={y(b)} r="3.5" className={up ? "mv-upd" : "mv-downd"} />
        </svg>
        <span className="mv-n">{fmtNum(Math.round(a))} → <b>{fmtNum(Math.round(b))}</b></span>
        <Chg t={x.t} />
      </button>
    );
  };

  return (
    <>
      {last && (
        <div className="mv-bar">
          <p className="page-note" style={{ margin: 0 }}>
            Fiscal years run July to June and are named for the year they end: {short(last)} = Jul 20{String(Number(last.slice(-2)) - 1).padStart(2, "0")} – Jun 20{last.slice(-2)}.
          </p>
          <span className="seg sm" role="group" aria-label="Rank growing and shrinking by">
            <button type="button" aria-pressed={by === "units"} onClick={() => setBy("units")}>Most units</button>
            <button type="button" aria-pressed={by === "pct"} onClick={() => setBy("pct")}>Biggest %</button>
          </span>
        </div>
      )}
      {(growing.length > 0 || shrinking.length > 0) && prev && last && (
        <div className="movers">
          <div className="mbox">
            <h4>Growing most · {short(last)} vs {short(prev)}</h4>
            {growing.length ? growing.map((x) => <Mover key={x.key} x={x} />) : <span className="muted">Nothing grew.</span>}
          </div>
          <div className="mbox">
            <h4>Shrinking most · {short(last)} vs {short(prev)}</h4>
            {shrinking.length ? shrinking.map((x) => <Mover key={x.key} x={x} />) : <span className="muted">Nothing shrank.</span>}
          </div>
        </div>
      )}

      <div className="tablewrap">
        <table className="dt">
          <thead>
            <tr>
              <th>{byPart ? "Part" : "Product line"}</th>
              {full.map((fy) => <th key={fy}>{short(fy)}</th>)}
              <th>{last && prev ? `${short(last)} vs ${short(prev)}` : "Change"}</th>
              <th className="l">{short(currentFy)} so far → projected</th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => {
              const so = x.t.used[currentFy] ?? 0;
              return (
                <tr key={x.key} className={`click${sel === x.key ? " sel" : ""}`} onClick={() => (byPart ? onBreakdown(x.key, last) : setSel(x.key))}>
                  <td className="l">
                    <span className="code">{x.label}</span>
                    {byPart ? partName(x.key) && <span className="name">{partName(x.key)}</span> : <span className="sub"> · {x.rows.length} part{x.rows.length > 1 ? "s" : ""}</span>}
                  </td>
                  {full.map((fy, i) => {
                    const v = Math.round(x.t.used[fy] ?? 0);
                    return <td key={fy}>{i === full.length - 1 ? <b>{fmtNum(v)}</b> : fmtNum(v)}</td>;
                  })}
                  <td><Chg t={x.t} /></td>
                  <td className="l">
                    {x.t.proj != null ? (
                      <span className="pace">
                        <span className="pace-t"><i style={{ width: `${Math.min(100, (so / x.t.proj) * 100)}%` }} /></span>
                        {fmtNum(Math.round(so))} <span className="sub">of ~{fmtNum(Math.round(x.t.proj))}</span>
                      </span>
                    ) : (
                      <>{fmtNum(Math.round(so))} <span className="sub">· can't project</span></>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <>
          <div className="panel-scrim" onClick={() => setSel(null)} aria-hidden="true" />
          <aside className="panel" role="dialog" aria-label={selected.label}>
            <div className="ph">
              <div>
                <h2>{selected.label}</h2>
                <div className="muted">{selected.rows.length} parts · {short(last)} vs {short(prev)} <Chg t={selected.t} /></div>
              </div>
              <button type="button" className="close" onClick={() => setSel(null)} aria-label="Close">✕</button>
            </div>
            <div className="box">
              <h4>Units used per fiscal year</h4>
              <YearChart t={selected.t} fys={[...full, currentFy]} currentFy={currentFy} yd={unit === "yd"} />
              <div className="yc-legend" style={{ marginTop: 8 }}>
                <span><i className="k-alone" />sold on its own</span>
                <span><i className="k-in" />inside umbrellas</span>
                <span><i className="k-cur" />{short(currentFy)} so far</span>
                <span><i className="k-proj" />projected</span>
              </div>
            </div>
            <div className="box">
              <div className="box-head">
                <h4>Its parts</h4>
                <span className="seg sm" role="group" aria-label="Group parts by">
                  <button type="button" aria-pressed={groupBy === "size"} onClick={() => setGroupBy("size")}>By size</button>
                  <button type="button" aria-pressed={groupBy === "finish"} onClick={() => setGroupBy("finish")}>{finishWord}</button>
                </span>
              </div>
              {partGroups.map((g) => (
                <div key={g.label} className="pgroup">
                  <div className="pgroup-head">
                    <b>{g.label}</b> <span className="muted">· {g.rows.length}</span>
                    <span className="r">
                      {fmtNum(Math.round(g.t.used[prev] ?? 0))} → <b>{fmtNum(Math.round(g.t.used[last] ?? 0))}</b> <Chg t={g.t} />
                    </span>
                  </div>
                  {g.rows.map((r) => {
                    const t = totalsOf([r], fys, currentFy);
                    const f = skuFacets(r.itemid);
                    const other = f ? (groupBy === "size" ? f.finish : f.size) : partName(r.itemid);
                    return (
                      <button key={r.itemid} type="button" className="lrow" onClick={() => onBreakdown(r.itemid, last)}>
                        <span><b>{other || r.itemid}</b><span className="s">{r.itemid}</span></span>
                        <span className="r">
                          {fmtNum(Math.round(r.fy_totals[prev] ?? 0))} → <b>{fmtNum(Math.round(r.fy_totals[last] ?? 0))}</b>
                          <span className="s"><Chg t={t} /></span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <p className="fine">Click a part to see how its number is built.</p>
          </aside>
        </>
      )}
    </>
  );
}
